import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { JobLanguage, CEFRLevel } from "@/types/candidate";
import { toast } from "@/hooks/use-toast";

export const useJobLanguages = (jobId?: string) => {
  const [languages, setLanguages] = useState<JobLanguage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLanguages = async () => {
    if (!jobId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('job_languages')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at');

      if (error) throw error;
      setLanguages(data || []);
    } catch (error) {
      console.error('Error fetching job languages:', error);
      toast({
        title: "Error",
        description: "Failed to load job languages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLanguages = async (updatedLanguages: JobLanguage[]) => {
    if (!jobId) return;

    try {
      // Delete existing languages that are not in the updated list
      const existingIds = languages.map(l => l.id).filter(Boolean);
      const updatedIds = updatedLanguages.map(l => l.id).filter(Boolean);

      const toDelete = existingIds.filter(id => !updatedIds.includes(id));
      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('job_languages')
          .delete()
          .eq('job_id', jobId)
          .in('id', toDelete);

        if (deleteError) throw deleteError;
      }

      // Update or insert languages
      for (const lang of updatedLanguages) {
        if (lang.id) {
          // Update existing
          const { error } = await supabase
            .from('job_languages')
            .update({
              language: lang.language,
              language_code: lang.language_code,
              min_cefr_level: lang.min_cefr_level,
              is_mandatory: lang.is_mandatory,
              is_user_owned: lang.is_user_owned
            })
            .eq('id', lang.id);

          if (error) throw error;
        } else {
          // Insert new
          const { error } = await supabase
            .from('job_languages')
            .insert({
              job_id: jobId,
              language: lang.language,
              language_code: lang.language_code,
              min_cefr_level: lang.min_cefr_level,
              is_mandatory: lang.is_mandatory,
              is_user_owned: lang.is_user_owned
            });

          if (error) throw error;
        }
      }

      await fetchLanguages(); // Refresh the list
      toast({
        title: "Success",
        description: "Job languages updated successfully",
      });
    } catch (error) {
      console.error('Error updating job languages:', error);
      toast({
        title: "Error",
        description: "Failed to update job languages",
        variant: "destructive",
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, [jobId]);

  return {
    languages,
    loading,
    updateLanguages,
    refetch: fetchLanguages
  };
};